'use client';

import { useTranslations } from "next-intl";
import classnames from 'clsx';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import PersonalList from '@/features/notifications/components/personal-list';
import PlatformList from '@/features/notifications/components/platform-list';
import { useEffect, useState } from "react";
import {
  useNotificationUnreadNum,
  setNotificationAllRead,
  useInvalidateNotificationUnreadNum,
  type NotificationItem,
} from "@/features/notifications/services";
import { NotificationType } from "@/features/notifications/constant";
import useIsLogin from "@/features/notifications/hooks/useIsLogin";
import { track } from "@/utils/track";
import { useAtom } from 'jotai';
import { unreadCountInfoAtom } from '@/store/notifications';
import clsx from "clsx";
import { NotificationEventType } from "@/features/notifications/constant";
import { TRACK_MSG_TYPES, TRACK_MSG_TYPE_VALUE } from './constant';
import IconFont from "@/components/ui/iconFont";


const NoticeDot = ({ className, noticeNum }: { className?: string, noticeNum: number | string; }) => {
  return (
    <p className={clsx("bg-accent-red min-w-[.8125rem] h-3 rounded-full text-[.68755rem] text-white flex items-center justify-center ml-[2px] px-[3px] leading-none", className)}>
      <span className="mt-[1px]">{noticeNum}</span>
    </p>
  );
};

const NotificationDrawer = NiceModal.create(() => {
  const modal = useModal();
  const t = useTranslations('notifications');
  const { isLogin } = useIsLogin();
  const invalidateNotificationUnreadNum = useInvalidateNotificationUnreadNum();

  const [unreadCountInfo, setUnreadCountInfo] = useAtom(unreadCountInfoAtom);

  const [msgType, setMsgType] = useState<number>(NotificationType.Platform);

  const [readAll, setReadAll] = useState({
    platform: false,
    personal: false
  });

  const handleClose = () => {
    modal.hide();
  };

  // 使用 Hook 获取未读数量，仅在用户登录时请求
  const { data: unreadNumData, refetch: refetchUnreadNum } = useNotificationUnreadNum({
    enabled: isLogin,
  });

  useEffect(() => {
    if (modal.visible) {
      refetchUnreadNum();
    }
  }, [modal.visible, refetchUnreadNum]);

  function getUnreadNumByType(type: number): number | string {
    if (type === NotificationType.Platform) {
      return unreadCountInfo.platformUnreadCount > 99 ? '99+' : unreadCountInfo.platformUnreadCount;
    } else if (type === NotificationType.Personal) {
      return unreadCountInfo.personalUnreadCount > 99 ? '99+' : unreadCountInfo.personalUnreadCount;
    }
    return 0;
  }

  function toggleMsgType(type: number) {
    setMsgType(type);
    refetchUnreadNum();
  }

  async function setNotificationReadAll() {
    // 只在登录时才允许设置已读
    if (!isLogin) return;

    const res = await setNotificationAllRead({ msg_type: msgType });
    if (res?.status === 1) {
      if (msgType === NotificationType.Personal) {
        setUnreadCountInfo({
          ...unreadCountInfo,
          personalUnreadCount: 0,
        });
        setReadAll(pre => ({ ...pre, personal: true }));
      } else {
        setUnreadCountInfo({
          ...unreadCountInfo,
          platformUnreadCount: 0,
        });
        setReadAll(pre => ({ ...pre, platform: true }));
      }

      // 使缓存失效，通知其他组件更新未读消息数量
      invalidateNotificationUnreadNum();
    }
  }

  function decUnreadNumAction(type: number) {
    if (type === NotificationType.Platform) {
      setUnreadCountInfo(pre => ({ ...pre, platformUnreadCount: pre.platformUnreadCount > 0 ? pre.platformUnreadCount - 1 : 0 }));
    } else if (type === NotificationType.Personal) {
      setUnreadCountInfo(pre => ({ ...pre, personalUnreadCount: pre.personalUnreadCount > 0 ? pre.personalUnreadCount - 1 : 0 }));
    }

    // 使缓存失效，通知其他组件更新未读消息数量
    invalidateNotificationUnreadNum();
  }

  function trackRead(msgItem: NotificationItem) {
    console.log('msgItem', msgItem);
    const { event_type, id, msg_config_id } = msgItem || {} as NotificationItem;
    if (TRACK_MSG_TYPES.includes(event_type)) {
      const trackParams: Record<string, string> = {
        message_type: TRACK_MSG_TYPE_VALUE[event_type],
        message_push_id: id,
        display_format: 'Notification Center',
        button_name: 'Mark as Read'
      };
      if (event_type === NotificationEventType.Platform) {
        trackParams.message_id = msg_config_id;
      }

      // 上报埋点
      track('inbox_click', trackParams);
    }
  }

  function handleJumpClick(msgItem: NotificationItem, btnName: string) {
    console.log('handleJumpClick', msgItem);
    console.log('btnName', btnName);
    const { event_type, id, msg_config_id } = msgItem || {} as NotificationItem;
    if (TRACK_MSG_TYPES.includes(event_type)) {
      const trackParams: Record<string, string> = {
        message_type: TRACK_MSG_TYPE_VALUE[event_type],
        message_push_id: id,
        display_format: 'Notification Center',
        button_name: btnName
      };
      if (event_type === NotificationEventType.Platform) {
        trackParams.message_id = msg_config_id;
      }

      // 上报埋点
      track('inbox_click', trackParams);
    }
  }

  // 个人消息列表组件里面发起跳转部分页面时需要关闭站内信抽屉
  function closeDrawer() {
    handleClose();
  }

  // 当从 Hook 获取到数据时，更新本地状态
  useEffect(() => {
    if (unreadNumData) {
      setUnreadCountInfo({
        allUnreadCount: unreadNumData.all_unread_count,
        platformUnreadCount: unreadNumData.platform_unread_count,
        personalUnreadCount: unreadNumData.personal_unread_count,
      });
    }
  }, [unreadNumData]);

  return (
    <Drawer
      open={modal.visible}
      onOpenChange={(open) => !open && handleClose()}
      onClose={handleClose}
      direction="bottom"
    >
      <DrawerTitle></DrawerTitle>
      <DrawerContent className="h-[81vh] border-none bg-surface flex flex-col items-center">
        <div className="w-full h-full">
          <div className="w-full flex items-center justify-between pt-[5px] h-[59px] box-border px-[20px] relative">
            <div onClick={setNotificationReadAll}>
              {
                !!getUnreadNumByType(msgType) && (
                  <div className="flex items-center">
                    <div className="text-[11px] text-accent-blue font-medium pressable active:scale-90">{t('markAllAsRead')}</div>
                  </div>
                )
              }
            </div>
            <span className="font-semibold absolute top-[25px] left-[50%] translate-x-[-50%] leading-[12px]">{t('title')}</span>
            <div className="w-[34px] h-[34px] flex items-center justify-end pressable" onClick={handleClose}>
              <IconFont name="close" size={13} color="#a9b5cb" />
            </div>
          </div>
          <div className="h-[35px] mx-[12px] mb-[5px] bg-[rgba(0,0,0,0.35)] rounded-[8px] flex items-center text-[13px] font-medium">
            {
              [NotificationType.Platform, NotificationType.Personal].map(item => {
                return <div
                  onClick={() => toggleMsgType(item)}
                  key={item}
                  className={classnames('flex-1 text-center h-full flex items-center pl-[10px]', msgType === item ? 'bg-accent-blue rounded-[8px] text-tertiary' : '')}>
                  <div className="flex-1 flex justify-center relative">
                    {item === NotificationType.Personal && <span className={classnames(msgType === item ? 'text-white' : 'text-tertiary')} style={{ textShadow: msgType === item ? '-1px -1px #0081c366, 1px -1px #0081c366, -1px 1px #0081c366, 1px 1px #0081c366' : '' }}>{t('personal')}</span>}
                    {item === NotificationType.Platform && <span className={classnames(msgType === item ? 'text-white' : 'text-tertiary')} style={{ textShadow: msgType === item ? '-1px -1px #0081c366, 1px -1px #0081c366, -1px 1px #0081c366, 1px 1px #0081c366' : '' }}>{t('platform')}</span>}
                    {
                      !!getUnreadNumByType(item) &&
                      <NoticeDot noticeNum={getUnreadNumByType(item)} />
                    }
                  </div>

                </div>;
              })
            }
          </div>
          {
            msgType === NotificationType.Personal ?
              <PersonalList decUnreadNumAction={decUnreadNumAction} closeDrawer={closeDrawer} trackRead={trackRead} hasReadAll={readAll.personal} onJumpClick={handleJumpClick} /> :
              <PlatformList decUnreadNumAction={decUnreadNumAction} trackRead={trackRead} hasReadAll={readAll.platform} onJumpClick={handleJumpClick} />
          }
        </div>
      </DrawerContent>
    </Drawer>
  );
});

NotificationDrawer.displayName = 'NotificationDrawer';

export const showNotificationDrawer = (): Promise<undefined> => {
  return NiceModal.show(NotificationDrawer);
};